import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHeroSentinel } from "../../../shared/hooks/use-hero-visible";
import { conciergeActions } from "../../ai-concierge";
import { panelStyle } from "../../../shared/ui/card-frame";
import { useCategories } from "../hooks/use-categories";
import { playCategoryFx } from "../../../shared/ui/category-tap-fx";

import { Icon } from "@majourneys/icons";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE, CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";

const HERO_BAR_RADIUS = 26;
const GOLD_DARK = "#8A6425";
const HERO_PROMPTS = [
  {
    i18nKey: "home.hero_prompts.daytrips_marrakech",
    fallback: "Daytrips from Marrakech",
  },
  {
    i18nKey: "home.hero_prompts.pools_agafay",
    fallback: "Pools in Agafay",
  },
  {
    i18nKey: "home.hero_prompts.workshops_agadir",
    fallback: "Workshops in Agadir",
  },
  {
    i18nKey: "home.hero_prompts.activities_casablanca",
    fallback: "Activities in Casablanca",
  },
] as const;

export function HeroSection() {
  const { t, i18n } = useTranslation();
  const apiLang = i18n.language;
  const isRtl = apiLang === "ar";
  const [query, setQuery] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasQuery = query.trim().length > 0;
  const heroSentinelRef = useHeroSentinel();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = query.trim();
    setQuery("");
    if (q) {
      conciergeActions.openWith(q);
      return;
    }
    conciergeActions.open();
  };

  /** A starter prompt goes into the box, focused with the caret at its end. */
  const fillWith = (text: string) => {
    setQuery(text);
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    window.setTimeout(() => input.setSelectionRange(text.length, text.length), 0);
  };

  return (
    <section className="relative">
      {/* No background of its own. The homepage paints one continuous wash
          behind every section (see BackgroundOrbs); a gradient here would
          end at the hero's bottom and draw a seam across the page. */}
      <div>
        {/* Sized by its content, not a forced 100vh. Centring the content in a
            full-viewport box left dead space above the eyebrow and below the
            chips on tall screens. */}
        <div className="relative flex items-center overflow-x-clip">
          <div className="relative mx-auto w-full max-w-[1320px] px-4 pb-6 pt-6 sm:px-6 lg:px-8 lg:pt-8">
            <div className="relative z-10 mx-auto flex w-full max-w-[960px] flex-col items-center gap-4 text-center">
              {/* The "Morocco, curated by locals" eyebrow chip was removed
                  (owner, 2026-09-08). The `home.hero_eyebrow` string stays in
                  all seven locales rather than being deleted: it is one line
                  per file, and pulling it means seven files churn for nothing
                  if the chip comes back. */}
              <h1 className="font-heading text-[36px] font-bold leading-[1.1] tracking-[-0.02em] text-[#110E39] sm:text-[42px] md:text-[50px] lg:text-[56px]">
                {t("home.hero_title_1")}{" "}
                {/* The navy chip stays, and the word inside it is a gold
                    gradient rather than one flat gold, so the letters catch
                    light across their width the way the metal would.

                    Owner, 2026-09-08: the first version ran from a near-white
                    champagne (#F7E3BE) and read as WHITE letters with gold
                    edges rather than as gold. The palest stop is now the brand
                    gold itself and the range runs downward from it into bronze,
                    which is how gold leaf actually behaves — it goes darker in
                    shadow, it does not go white. One turn back up at 62% keeps
                    the sweep from looking like a flat fade.

                    The chip is what makes this safe. Every stop is gold, and
                    gold on white is about 1.9:1 — failing even the 3:1 large
                    display type is allowed. On the navy the palest stop is past
                    7:1 and the deepest past 4.5:1.

                    `color` holds the flat #E5B265 underneath, so a browser
                    without background-clip:text renders the word rather than
                    an invisible one. */}
                <span className="relative inline-block">
                  {/* Chip and letters are two elements, not two background
                      layers on one. A layered `background-clip: text,
                      padding-box` is dropped by Firefox, which then painted
                      the gold across the whole chip and hid the word. A
                      single-layer `background-clip: text` works everywhere.

                      The navy is the same 135° indigo-to-ink ramp the primary
                      buttons wear, so the chip is lit like everything else. */}
                  <span
                    className="relative z-10 inline-block rounded-[10px] px-[8px] leading-[1.05] md:px-[10px]"
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)",
                    }}
                  >
                    <span
                      style={{
                        color: "#E5B265",
                        backgroundImage:
                          "linear-gradient(100deg, #E8BE7C 0%, #D6A257 34%, #EFCF95 62%, #A8792F 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {t("home.hero_title_highlight")}
                    </span>
                  </span>
                  <HeroStickerIcon />
                </span>{" "}
                {t("home.hero_title_3")}
              </h1>

              <div className="flex w-full flex-col items-center gap-4">
                {/* Pewter kit #34 (owner, 2026-10-09): the box wears the band,
                    a gold spark at its start and the send coin inside it. The
                    coin dims while the box is empty; an empty send still opens
                    MaJourneys AI, as before. */}
                <form
                  ref={formRef}
                  role="search"
                  autoComplete="off"
                  onSubmit={handleSubmit}
                  className="w-full max-w-[760px]"
                  style={panelStyle(HERO_BAR_RADIUS)}
                >
                  <div className="flex items-center gap-2.5 p-1.5 ps-5" dir={isRtl ? "rtl" : "ltr"}>
                    <Icon
                      name="ri-sparkling-2-fill"
                      className="shrink-0 text-[17px]"
                      style={{ color: GOLD_DARK }}
                      aria-hidden="true"
                    />
                    <input
                      ref={inputRef}
                      type="text"
                      name="hero_search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const q = query.trim();
                          setQuery("");
                          if (q) conciergeActions.openWith(q);
                          else conciergeActions.open();
                        }
                      }}
                      placeholder={t("home.hero_ai_placeholder", {
                        defaultValue: "Ask AI anything about your trip…",
                      })}
                      aria-label={t("home.hero_ai_aria", { defaultValue: "Ask MaJourneys AI" })}
                      className="h-12 w-full min-w-0 flex-1 border-0 bg-transparent text-start text-[16px] font-medium text-[#110E39] outline-none placeholder:font-normal placeholder:text-[#5a5d77] sm:text-[17px]"
                    />
                    <button
                      type="submit"
                      aria-label={t("home.hero_ai_aria", { defaultValue: "Ask MaJourneys AI" })}
                      className="mj-press grid h-11 w-11 shrink-0 place-items-center rounded-full text-white transition-opacity duration-200"
                      style={{
                        background: BEVEL_NAVY_FACE,
                        opacity: hasQuery ? 1 : 0.4,
                        boxShadow: `0 8px 18px -10px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
                      }}
                    >
                      <Icon
                        name={isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line"}
                        className="text-[18px]"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </form>

                {/* A starter fills the box rather than sending, so the guest
                    can add dates or a budget before asking. */}
                <div className="flex flex-wrap justify-center gap-2">
                  {HERO_PROMPTS.map((prompt) => {
                    const label = t(prompt.i18nKey, { defaultValue: prompt.fallback });
                    return (
                      <button
                        key={prompt.i18nKey}
                        type="button"
                        onClick={() => fillWith(label)}
                        className="mj-press min-h-9 rounded-full px-3.5 py-1.5 font-sans text-[13px] font-semibold text-[#110E39] transition-shadow duration-200 hover:shadow-[0_6px_16px_-10px_rgba(17,14,57,0.45)]"
                        style={{ background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}` }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={heroSentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-px w-px"
      />
    </section>
  );
}

/**
 * Time each category icon stays on the chip before the next one arrives.
 * 2400ms plus 20% (owner, 2026-10-30), so each landing effect has room to
 * finish before the next icon comes in.
 */
const STICKER_INTERVAL_MS = 2880;

/** Categories left out of the rotation (owner, 2026-10-30: not trips). */
const STICKER_SKIP = new Set(["trips"]);

/**
 * How each category's icon arrives, picked for what the category feels like
 * (owner, 2026-10-29). Classes live in lib/tailwind.css. A slug that is not
 * listed gets the plain spin-in.
 */
const STICKER_ENTRANCE: Record<string, string> = {
  activities: "mj-sticker-drive", // buggy drives in over a bump
  boats: "mj-sticker-sail", // sails in on a wave and rocks
  excursions: "mj-sticker-offroad", // jeep jolts over rough ground
  pools: "mj-sticker-splash", // cannonball drop and squish
  spas: "mj-sticker-zen", // slow, soft fade in
  stays: "mj-sticker-pillow", // settles down softly
  restaurants: "mj-sticker-cloche", // cloche lowered onto the chip, then lifted
  workshops: "mj-sticker-wheel", // spins like on a pottery wheel
};

/**
 * How long each entrance runs, delay included (lib/tailwind.css), so the
 * landing effect starts as the icon settles. A timer rather than
 * animationend: a throttled or background tab may never deliver the event.
 */
const ENTRANCE_MS: Record<string, number> = {
  "mj-sticker-spin": 820,
  "mj-sticker-drive": 1020,
  "mj-sticker-sail": 1220,
  "mj-sticker-offroad": 1120,
  "mj-sticker-splash": 920,
  "mj-sticker-zen": 1320,
  "mj-sticker-pillow": 1020,
  "mj-sticker-cloche": 1020,
  "mj-sticker-wheel": 1220,
};

/**
 * Icons whose art fills less of its frame than the rest, scaled so every
 * sticker reads as the same size. Measured from the visible pixels of each
 * file: most fill 92-98% of the box, the bed 84%, the vase about 60%.
 */
const STICKER_SCALE: Record<string, number> = {
  stays: 1.12,
  workshops: 1.45,
};

/**
 * Option C from the hero mockups (owner, 2026-10-29): the category 3D icons
 * take turns on the gold chip's top corner, each arriving in its own way and
 * the outgoing one floating up and away, so the corner is never empty. The
 * title itself never moves.
 *
 * Once an icon has landed it plays its category's tap effect (owner,
 * 2026-10-30): the pool splashes, the boat rides a wave, the spa steams, the
 * cloche lets its steam out. Same effects as the category tabs
 * (shared/ui/category-tap-fx.ts).
 *
 * Purely decorative, so it is aria-hidden and the h1 text stays exactly what
 * crawlers and screen readers read today. The first icon renders on the
 * server; the rotation starts after mount. Reduced motion keeps the first
 * icon, still, and never cycles.
 */
function HeroStickerIcon() {
  const { data } = useCategories();
  const stickers = (
    (data.data as Array<{ slug: string; icon?: string | null }> | undefined) ?? []
  ).filter(
    (c): c is { slug: string; icon: string } =>
      !!c.icon && /^https?:\/\//.test(c.icon) && !STICKER_SKIP.has(c.slug),
  );
  const [turn, setTurn] = useState(0);
  const count = stickers.length;
  const iconsKey = stickers.map((c) => c.icon).join("|");

  useEffect(() => {
    if (count < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Warm every icon first, so a turn never animates an empty frame.
    for (const src of iconsKey.split("|")) {
      const img = new Image();
      img.src = src;
    }
    const id = window.setInterval(() => setTurn((t) => t + 1), STICKER_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [count, iconsKey]);

  if (count === 0) return null;
  const current = stickers[turn % count];
  // Turn 0 is the server render: nothing is leaving yet.
  const leaving = turn > 0 ? stickers[(turn - 1) % count] : null;
  return (
    <span
      aria-hidden
      className="mj-sticker-host pointer-events-none absolute -end-[0.42em] -top-[0.5em] z-20 h-[0.95em] w-[0.95em]"
    >
      {leaving && <Sticker key={`out-${turn}`} sticker={leaving} className="mj-sticker-out" />}
      <Sticker
        key={`in-${turn}`}
        sticker={current}
        // The very first icon is already there on load; it only animates once
        // the rotation has started.
        className={turn > 0 ? (STICKER_ENTRANCE[current.slug] ?? "mj-sticker-spin") : ""}
        landed={turn > 0}
      />
    </span>
  );
}

function Sticker({
  sticker,
  className,
  landed = false,
}: {
  readonly sticker: { slug: string; icon: string };
  readonly className: string;
  /** Play the category's effect once the entrance has finished. */
  readonly landed?: boolean;
}) {
  const stage = useRef<HTMLSpanElement>(null);
  const scale = STICKER_SCALE[sticker.slug];
  useEffect(() => {
    if (!landed) return;
    const id = window.setTimeout(
      () => playCategoryFx(sticker.slug, stage.current),
      ENTRANCE_MS[className] ?? 900,
    );
    return () => window.clearTimeout(id);
  }, [landed, className, sticker.slug]);
  return (
    // Three layers so no two transforms fight: the size correction on the
    // outer span, the landing effect on the middle one, and the entrance plus
    // the resting tilt on the image.
    <span ref={stage} className="absolute inset-0" style={scale ? { transform: `scale(${scale})` } : undefined}>
      <span className="mjfx-icon block h-full w-full">
        <img
          src={sticker.icon}
          alt=""
          decoding="async"
          className={`mj-sticker h-full w-full object-contain ${className}`}
        />
      </span>
    </span>
  );
}
